/**
 * design-tokens.js — single source of truth for visual constants.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Before this audit, the codebase had:
 *   • 23 places hard-coding the WAVE primary color (#00D4FF)
 *   • 17 places hard-coding the WAVE accent (#FF4D94)
 *   • 11 places hard-coding the CMD purple (#A78BFA)
 *   • 47 different shade variants of #181A20 (the panel background)
 *   • 6 places re-defining a "score band → color" helper
 *
 * That drift made the brand look slightly different on every page and
 * made design changes (e.g. "make the panels 10% lighter") into a sweep
 * through 50+ files.
 *
 * This module is the new single source of truth. New components MUST
 * import from here; existing components should be migrated as touched.
 *
 * HOW TO USE
 * ----------
 *   import { COLORS, SCORE_COLOR, RADIUS, SHADOW, MOTION } from '../lib/design-tokens';
 *   <div style={{ background: COLORS.panel.bg, border: `1px solid ${COLORS.panel.border}` }} />
 *
 * AUDIT NOTES
 * -----------
 * The token names match the design intent, not the hex value, so we can
 * adjust the actual hex later without renaming everything. e.g. the
 * primary brand color is `COLORS.brand.primary`, not `COLORS.teal`.
 */

// ─── Brand palette ─────────────────────────────────────────────────────
export const COLORS = {
  brand: {
    /** Teal — DSP primary. Used for: links, primary buttons, Reach KPIs. */
    primary:        '#00D4FF',
    primarySoft:    'rgba(0,212,255,0.15)',
    primaryBorder:  'rgba(0,212,255,0.40)',

    /** Pink — SSP / amplification accent. Used for: Flight Deck, Engagement KPIs. */
    accent:         '#FF4D94',
    accentSoft:     'rgba(255,77,148,0.14)',
    accentBorder:   'rgba(255,77,148,0.35)',

    /** Purple — CMD layer. Used for: patches, recommendations, lenses. */
    cmd:            '#A78BFA',
    cmdSoft:        'rgba(167,139,250,0.16)',
    cmdBorder:      'rgba(167,139,250,0.33)',
  },

  // ─── Status palette (universal) ──────────────────────────────────────
  status: {
    success:        '#4ade80',           // green-400 — KPI met, healthy
    warning:        '#fbbf24',           // amber-400 — KPI at risk, intervene
    danger:         '#ef4444',           // red-500 — KPI broken
    neutral:        '#9CA3AF',           // gray-400 — no signal yet
    live:           '#4ade80',           // pulsing green for LIVE indicators
  },

  // ─── Surface palette (dark mode primary) ─────────────────────────────
  // All panels in the app sit on a dark background. These are the
  // canonical surface tones — do NOT introduce new ones.
  surface: {
    canvas:         '#0a0a0a',           // outermost page background
    canvasInset:    '#0F0F0F',           // panels on canvas (FlightDeck, sidebars)
    panel:          '#181A20',           // standard card background
    panelRaised:    '#1F222A',           // hero cards, modals
    border:         '#2E3240',           // standard panel border
    borderSoft:     '#1f1f1f',           // softer divider lines
    overlay:        'rgba(10,10,10,0.55)', // tinted inset (KpiTile, etc)
  },

  // ─── Text palette ────────────────────────────────────────────────────
  text: {
    primary:        '#FFFFFF',           // headings, score numbers
    secondary:      '#cbd5e1',           // body text in panels
    tertiary:       '#9CA3AF',           // de-emphasized labels
    muted:          '#6B7280',           // disabled, placeholder
    inverse:        '#0a0a0a',           // on bright-color buttons
  },
};


// ─── Score → color helper ───────────────────────────────────────────────
// Used wherever a 0-100 metric needs a band color: Wave Pulse, composites,
// patch health bucket indicators. Three bands: <45 = warning,
// 45-74 = brand primary, ≥75 = success. Adjust band cutoffs ONCE here
// instead of in every component.
export function scoreColor(score) {
  if (score >= 75) return COLORS.status.success;
  if (score >= 45) return COLORS.brand.primary;
  return COLORS.status.warning;
}


// ─── Spacing scale (Tailwind-aligned for migration ease) ───────────────
export const SPACING = {
  xs:  '0.25rem',     // 4px
  sm:  '0.5rem',      // 8px
  md:  '0.75rem',     // 12px
  lg:  '1rem',        // 16px
  xl:  '1.5rem',      // 24px
  xxl: '2rem',        // 32px
};


// ─── Border radius ──────────────────────────────────────────────────────
export const RADIUS = {
  sm:  '0.5rem',      // 8px — tags, pills
  md:  '0.75rem',     // 12px — buttons, slim bands
  lg:  '1rem',        // 16px — cards
  xl:  '1.5rem',      // 24px — hero panels, modals
};


// ─── Shadows (sparingly — dark UI relies more on borders + glow) ───────
export const SHADOW = {
  glow:    (color) => `0 0 12px ${color}99, 0 0 24px ${color}33`,
  panel:   '0 12px 32px -16px rgba(0,0,0,0.6)',
  inset:   'inset 0 1px 0 rgba(255,255,255,0.04)',
};


// ─── Motion ─────────────────────────────────────────────────────────────
// Standard durations + easings. Use these instead of bespoke values so
// the whole app feels temporally cohesive.
export const MOTION = {
  duration: {
    fast:   '150ms',         // hover states, micro-interactions
    base:   '300ms',         // standard transitions
    slow:   '600ms',         // pulse-ring fills, big state changes
  },
  easing: {
    standard:  'cubic-bezier(.22,1,.36,1)',   // smooth ease-out (most things)
    enter:     'cubic-bezier(0,0,0.2,1)',     // entrance animations
    exit:      'cubic-bezier(0.4,0,1,1)',     // exit animations
  },
};


// ─── Typography ─────────────────────────────────────────────────────────
// We intentionally don't ship a `fontFamily` token because we want to
// inherit from the Tailwind base config (where the brand font lives).
// These are only for SIZE + WEIGHT + TRACKING patterns we re-use enough
// to be worth centralizing.
export const TYPE = {
  /** Massive score numbers (Wave Pulse hero, composite cards). */
  scoreHero:    { fontWeight: 900, letterSpacing: '-0.04em', fontVariantNumeric: 'tabular-nums' },
  scoreMedium:  { fontWeight: 900, letterSpacing: '-0.025em', fontVariantNumeric: 'tabular-nums' },

  /** Section labels — UPPERCASE WIDE-TRACKED. */
  label:        { fontWeight: 900, letterSpacing: '0.18em', textTransform: 'uppercase' },
  labelTight:   { fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase' },

  /** Body text. */
  body:         { fontWeight: 400, lineHeight: 1.5 },
  bodyEmphasis: { fontWeight: 700, lineHeight: 1.5 },
};


// ─── Z-index scale (single source of truth) ─────────────────────────────
// Migrating individual `z-50` etc to these named layers reduces stacking
// bugs (modal-over-tooltip-over-toast etc).
export const Z = {
  base:        1,
  raised:      10,
  sticky:      20,
  dropdown:    30,
  drawer:      40,
  modal:       50,
  toast:       60,
  tooltip:     70,
};
